import React, { useEffect, useRef, useState, useMemo } from "react";
import KpiCard from "components/NewComponents/NewKpiCard";
import Header from "components/Header";
// Removed: import Chart from "chart.js/auto"; - ChartJS import replaces this
import { Line, Bar } from 'react-chartjs-2'; // Consolidated line and bar import
// Note: Ensure L is imported from 'leaflet'
import { MapContainer, GeoJSON, useMap, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from 'leaflet';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    // FIX 1: Import required elements for Line/Area chart (StockOutAreaChart)
    PointElement,
    LineElement,
    Filler,
} from 'chart.js';

// --- Consolidated Chart.js Registration (FIXED) ---
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);
// ----------------------------------------------------


// 1. HELPER COMPONENT: MapBoundsUpdater (FIXED DEFINITION)
const MapBoundsUpdater = ({ geoData }) => {
    const map = useMap();

    const bounds = useMemo(() => {
        if (geoData) {
            const geoJsonLayer = L.geoJSON(geoData);
            return geoJsonLayer.getBounds();
        }
        return null;
    }, [geoData]);

    useEffect(() => {
        if (bounds) {
            map.fitBounds(bounds);
        }
    }, [map, bounds]);

    return null;
};

// 2. HELPER FUNCTION: generateRandomMarkers (FIXED DEFINITION)
const generateRandomMarkers = (count, baseLabel) => {
    const markers = [];
    const generationCount = count + 20;
    for (let i = 0; i < generationCount; i++) {
        // Random latitude (15.5 to 22.0)
        const lat = 15.5 + Math.random() * 6.5;
        // Random longitude (72.5 to 80.0)
        const lon = 72.5 + Math.random() * 7.5;

        markers.push({
            id: `${baseLabel}-${i}`,
            position: [lat, lon],
            label: `${baseLabel} Initiative Point ${i + 1}`,
        });
    }
    return markers;
};

// 3. HELPER FUNCTION: isPointInMaharashtra (FIXED DEFINITION)
const isPointInMaharashtra = (lat, lon, geoData) => {
    if (!geoData || !geoData.features || geoData.features.length === 0) return false;

    const point = L.latLng(lat, lon);

    for (const feature of geoData.features) {
        const layer = L.geoJSON(feature);
        if (layer.getBounds().contains(point)) {
            return true;
        }
    }
    return false;
};

// 4. HELPER COMPONENT: MapLegend (FIXED DEFINITION)
const MapLegend = ({ markerColor, activeTab }) => {
    const choroplethData = [
        { color: '#D4EDDA', label: 'Target Achieved (Green Zone)' },
        { color: '#FFEEBA', label: 'Moderate Progress (Yellow Zone)' },
        { color: '#F8D7DA', label: 'Critical Gap (Red Zone)' }
    ];

    const choroplethTitle = activeTab === 'RMNCH+A'
        ? "District Performance (e.g., IMR/MMR Rate)"
        : "District Performance (e.g., TB Notification Rate)";

    const markerTitle = activeTab === 'RMNCH+A'
        ? "Maternal/Child Health Intervention Site"
        : "NCD Screening or Treatment Site";

    return (
        <div className="absolute bottom-4 right-4 bg-white p-3 rounded-lg border shadow-sm z-[1000] w-72">
            <h4 className="text-sm font-bold text-slate-700 mb-2">Map Legend: {activeTab} Focus</h4>

            <div>
                <p className="text-xs font-semibold text-slate-600 mb-1">Initiative Sites (Dots)</p>
                <div className="flex items-center text-xs text-slate-700">
                    <div style={{ backgroundColor: "#5B21B6" }} className="w-3 h-3 mr-2 rounded-full border border-white shadow-sm"></div>
                    <span>{markerTitle}</span>
                </div>
            </div>
        </div>
    );
};

// 5. HELPER COMPONENT: ScreeningProgressBars (FIXED DEFINITION)
const ScreeningProgressBars = ({ data }) => {
    return (
        <div className="flex flex-col space-y-4 pt-1 lg:pt-0">
            <h3 className="text-lg font-bold text-slate-700">Screening Coverage Targets</h3>
            {data.map((item) => (
                <div key={item.name} className="space-y-1">
                    <div className="flex justify-between items-center text-sm font-medium text-slate-600">
                        <span>{item.name}</span>
                        <span>{item.value}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className={`h-2.5 rounded-full transition-all duration-700 ${item.color}`}
                            style={{ width: `${item.value}%` }}
                        ></div>
                    </div>
                    <p className="text-xs text-slate-500">Target: {item.target}%</p>
                </div>
            ))}
        </div>
    );
};

// 6. HELPER COMPONENT: StyledDonutChart (FIXED)
const StyledDonutChart = ({ data }) => {
    const chartRef = useRef(null);
    const chartInstanceRef = useRef(null);

    const COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444'];

    const total = data.reduce((sum, item) => sum + item.value, 0);

    useEffect(() => {
        if (chartInstanceRef.current) {
            // ChartJS.register should be done once at the top, but the Doughnut component uses the global Chart import
            // Assuming "chart.js/auto" (or the direct Chart import) is needed for this specific implementation if not using react-chartjs-2's Doughnut wrapper.
            // Since ChartJS is used globally now, we can remove the old Chart import in the original file.
            chartInstanceRef.current.destroy();
        }

        if (!chartRef.current) return;

        // Using window.Chart since it's loaded via chart.js/auto or a similar mechanism, 
        // or assuming 'Chart' is passed into scope if the component is used outside of this file.
        // For consistency, we rely on the global Chart provided by the import.
        const Chart = window.Chart || (window.Chart = ChartJS);

        const ctx = chartRef.current.getContext("2d");

        chartInstanceRef.current = new Chart(ctx, {
            type: "doughnut",
            data: {
                labels: data.map(item => item.name),
                datasets: [
                    {
                        data: data.map(item => item.value),
                        backgroundColor: COLORS,
                        borderWidth: 2,
                        borderColor: "#fff",
                        hoverOffset: 8,
                    },
                ],
            },
            options: {
                responsive: true,
                cutout: "72%",
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: true },
                    datalabels: false // Commented out as datalabels plugin isn't registered
                },
            },
        });

        // Cleanup function
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
        };

    }, [data, total]); // Added total to dependency array

    return (
        <div className="p-4 bg-white rounded-xl shadow-md border border-slate-200 relative h-auto">
            <h3 className="text-lg font-bold text-slate-700">HR availability by cadre</h3>
            {/* Donut Chart */}
            <div className="relative flex justify-center items-center py-4">
                <div className="w-36 h-36">
                    <canvas ref={chartRef}></canvas>
                </div>

                {/* Center Total */}
                <div className="absolute flex flex-col items-center">
                    <span className="text-xs text-gray-500">Total</span>
                    <span className="text-2xl font-bold text-slate-900">
                        {total.toLocaleString()}
                    </span>
                </div>
            </div>

            {/* 🌟 Legend (BOTTOM) */}
            <div className="mt-3 flex justify-center gap-6 flex-wrap">
                {data.map((item, i) => (
                    <div
                        key={item.name}
                        className="flex items-center text-xs text-slate-700"
                    >
                        <div
                            className="w-3 h-3 mr-2 rounded-sm"
                            style={{ backgroundColor: COLORS[i % COLORS.length] }} // Use modulo for safety
                        />
                        {item.name}
                    </div>
                ))}
            </div>

        </div>
    );
};


// 7. HELPER COMPONENT: StockOutAreaChart (FIXED)
// 2. Mock Data
const stockOutTrendData = [
    { month: 'Jan', days: 5 },
    { month: 'Feb', days: 10 },
    { month: 'Mar', days: 3 },
    { month: 'Apr', days: 5 },
    { month: 'May', days: 9 },
    { month: 'Jun', days: 1 },
    { month: 'Jul', days: 3 },
    { month: 'Aug', days: 5 },
    { month: 'Sept', days: 9 },
    { month: 'Oct', days: 1 },
    { month: 'Nov', days: 9 },
    { month: 'Dec', days: 1 },
];

const ddata = {
    labels: stockOutTrendData.map(d => d.month),
    datasets: [
        {
            label: 'Total Stock-Out Days',
            data: stockOutTrendData.map(d => d.days),
            // Styling for the line
            borderColor: 'rgba(79, 70, 229, 1)', // Indigo
            pointBackgroundColor: 'rgba(79, 70, 229, 1)',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: 'rgba(79, 70, 229, 1)',
            tension: 0.3, // Curve the line slightly

            // Styling for the Area fill
            backgroundColor: 'rgba(79, 70, 229, 0.2)', // Light Indigo for the fill
            fill: 'start', // Start filling from the bottom (x-axis)
        },
    ],
};

// 3. Chart Options
const optionss = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false, // We only have one dataset

        },
        datalabels: false,
        title: {
            display: true,
            text: 'Monthly Trend of Total Stock-Out Days',
            font: {
                size: 18,
                weight: 'bold',
            },
            color: '#334155', // slate-700
        },
        tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
                label: function (context) {
                    let label = context.dataset.label || '';
                    if (label) {
                        label += ': ';
                    }
                    if (context.parsed.y !== null) {
                        label += new Intl.NumberFormat('en-US').format(context.parsed.y) + ' Days';
                    }
                    return label;
                }
            }
        }
    },
    scales: {
        x: {
            title: {
                display: true,
                text: 'Months (2025)',
                font: { weight: 'bold' },
            },
            grid: {
                display: false,
            }
        },
        y: {
            beginAtZero: true,
            title: {
                display: true,
                text: 'Total Stock-Out Days',
                font: { weight: 'bold' },
            },
            // Ensure only integer values are shown on the Y-axis
            ticks: {
                callback: function (value) {
                    if (value % 1 === 0) {
                        return value;
                    }
                }
            },
            grid: {
                color: 'rgba(203, 213, 225, 0.5)',
            }
        }
    }
};

// 4. React Component
const StockOutAreaChart = () => {
    return (
        <div className="p-4 bg-white rounded-xl shadow-md border border-slate-100 h-full">
            {/* Set a fixed height for a decent-looking chart */}
            <div style={{ height: '290px' }}>
                <Line data={ddata} options={optionss} />
            </div>
        </div>
    );
};

// 8. HELPER COMPONENT: TopDrugsBarChart (FIXED)
// 2. Mock Data
const drugData = [
    { name: 'Paracetamol 500mg', dispensed: 78500 },
    { name: 'Amoxicillin 250mg', dispensed: 62100 },
    { name: 'ORS (Packs)', dispensed: 55900 },
    { name: 'Metformin 500mg', dispensed: 41200 },
    { name: 'Iron Folic Acid', dispensed: 38800 },
];

const barChartData = {
    labels: drugData.map(d => d.name),
    datasets: [
        {
            label: 'Units Dispensed',
            data: drugData.map(d => d.dispensed),
            backgroundColor: [
                'rgba(79, 70, 229, 0.8)', // Indigo (for the top drug)
                'rgba(16, 185, 129, 0.8)', // Emerald
                'rgba(245, 158, 11, 0.8)', // Amber
                'rgba(59, 130, 246, 0.8)', // Blue
                'rgba(236, 72, 153, 0.8)', // Pink
            ],
            borderColor: [
                'rgba(79, 70, 229, 1)',
                'rgba(16, 185, 129, 1)',
                'rgba(245, 158, 11, 1)',
                'rgba(59, 130, 246, 1)',
                'rgba(236, 72, 153, 1)',
            ],
            borderWidth: 1,
            borderRadius: 4, // Adds rounded corners to bars
        },
    ],
};

// 3. Chart Options
const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false, // Allows you to control height
    indexAxis: 'y', // Makes it a horizontal bar chart
    plugins: {
        legend: {
            display: false, // We only have one dataset, so hide the legend
        },
        title: {
            display: false, // Title is handled by h3 outside the chart
        },
    },
    scales: {
        x: {
            beginAtZero: true,
            title: {
                display: true,
                text: 'Total Units Dispensed',
                font: { weight: 'bold' },
            },
            grid: {
                display: false,
            }
        },
        y: {
            title: {
                display: false,
            },
            grid: {
                // Keep horizontal lines visible but lighter
                color: 'rgba(203, 213, 225, 0.5)', // slate-300
            }
        }
    }
};

// 4. React Component
const TopDrugsBarChart = () => {
    return (
        <div className="px-4 pt-4 pb-2 bg-white rounded-xl shadow-md border border-slate-100 h-full">
            <h3 className="text-lg font-bold text-slate-700">Top 5 Drug Dispensed</h3>
            {/* Set a fixed height for a decent-looking chart */}
            <div style={{ height: '240px' }}>
                <Bar data={barChartData} options={barChartOptions} />
            </div>
        </div>
    );
};


// ----------------------------------------------------

// --- Styling Constants & Data ---
const mapBaseStyle = {
    fillColor: "#f0f0f0",
    color: "#9e9e9e",
    weight: 1,
    opacity: 1,
    fillOpacity: 1,
};
const markerColor = "#5B21B6";
const screeningData = [
    { name: "Oral Cancer Screening", value: 72, target: 80, color: "bg-emerald-500" },
    { name: "Breast Cancer Screening", value: 45, target: 80, color: "bg-yellow-500" },
    { name: "Cervical Cancer Screening", value: 58, target: 80, color: "bg-cyan-500" },
    { name: "Hypertension Screening", value: 85, target: 90, color: "bg-blue-500" },
    { name: "Diabetes Screening", value: 65, target: 90, color: "bg-fuchsia-500" },
];

// UPDATED DATA: HR Availability by Cadre (to match image style)
const hrAvailabilityData = [
    { name: 'Nurses', value: 4500 }, // Purple segment (41%)
    { name: 'Doctors', value: 3200 }, // Green segment (29%)
    { name: 'CHOs', value: 1200 }, // Orange segment (11%)
    { name: 'ANMs', value: 2100 }, // Remaining segment (19%) - added back ANMs to use all data
];


// ====================================================
// MAIN COMPONENT
// ====================================================
const IntegratedHealthDashboard = () => {
    const handleMenuClick = () => { /* Sidebar click */ };
    const [activeTab, setActiveTab] = useState("RMNCH+A");
    const [geoData, setGeoData] = useState(null);

    const initialMarkerPool = useMemo(() => ({
        // FIX: generateRandomMarkers is now defined above
        "RMNCH+A": generateRandomMarkers(50, "RMNCH+A Site"),
        "NCD": generateRandomMarkers(50, "NCD Screening Site"),
    }), []);

    useEffect(() => {
        fetch(process.env.PUBLIC_URL + "/geojson/1.geojson")
            .then((res) => res.json())
            .then((data) => setGeoData(data))
            .catch((err) => console.error("GeoJSON fetch error:", err));
    }, []);

    const finalMarkers = useMemo(() => {
        if (!geoData) return { "RMNCH+A": [], "NCD": [] };

        const filteredData = {};

        filteredData["RMNCH+A"] = initialMarkerPool["RMNCH+A"]
            // FIX: isPointInMaharashtra is now defined above
            .filter(m => isPointInMaharashtra(m.position[0], m.position[1], geoData))
            .slice(0, 30);

        filteredData["NCD"] = initialMarkerPool["NCD"]
            // FIX: isPointInMaharashtra is now defined above
            .filter(m => isPointInMaharashtra(m.position[0], m.position[1], geoData))
            .slice(0, 30);

        return filteredData;
    }, [geoData, initialMarkerPool]);
    const DateRangeLine = () => {
        const today = new Date();

        // 1st day of current month
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

        // Format function
        const formatDate = (date) =>
            date.toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });

        return (
            //   <div className="text-sm font-medium text-slate-700 py-2 px-3 bg-slate-50 border rounded-lg inline-block">

            //   </div>
            <span className="text-sm font-medium text-slate-700 px-3 py-1">Date {formatDate(firstDay)} - {formatDate(today)}</span>
        );
    };


    return (
        <div className="min-h-screen bg-gray-50">
            <Header
                onMenuClick={handleMenuClick}
                dashboardName="Integrated Health Dashboard"
            />
            <div className="text-end">
                <DateRangeLine />
            </div>
            <div className="w-full p-2 bg-gray-50">
                {/* KPI Cards (existing code here) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-2 mb-2">
                    <KpiCard title="Total Maternal Deaths" value="33" target="20" unit="deaths" trend={[28, 39, 31, 37, 31, 33, 36, 30]} />
                    <KpiCard title="Total Child Deaths" value="88" target="70" unit="deaths" trend={[86, 89, 81, 87, 88, 88, 87, 89]} />
                    <KpiCard title="Hypertension Screening" value="1.2M" target="1.5M" unit="tests" trend={[1000000, 1000000, 1100000, 1150000, 1100000, 1200000]} />
                    <KpiCard title="Diabetes Screening" value="900K" target="1M" unit="tests" trend={[850000, 860000, 870000, 880000, 890000, 900000]} />
                    <KpiCard title="TB Notification Rate" value="78%" target="85%" unit="" trend={[60, 72, 64, 70, 76, 68, 62, 61]} />
                    <KpiCard title="Treatment Initiation Rate (TIR)" value="91%" target="95%" unit="" trend={[88, 89, 90, 90, 91, 91]} />
                </div>

                {/* -------------------- Map and Side Panel Layout -------------------- */}
                {/* 🔹 ROW: Map + Side Panel (col-6) AND Charts (col-6) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">

                    {/* ⭐ LEFT SIDE — MAP & PROGRESS BARS (6 Columns) */}
                    <div className="bg-white lg:col-span-6 rounded-xl shadow-md border border-slate-100 p-4 h-[480px]">
                        {/* Tabs UI */}
                        <div className="flex border-b border-gray-200 mb-2">
                            {["RMNCH+A", "NCD"].map((tab) => (
                                <button
                                    key={tab}
                                    onClick={() => setActiveTab(tab)}
                                    className={`px-4 py-2 text-sm font-medium transition-colors duration-150 ${activeTab === tab
                                        ? "text-indigo-600 border-b-2 border-indigo-600"
                                        : "text-gray-500 hover:text-gray-700"
                                        }`}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        {/* Map + Progress Bars */}
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">

                            {/* MAP */}
                            <div className="lg:col-span-8 h-[400px] w-full relative">
                                {geoData && (
                                    <MapContainer
                                        center={[19.5, 76.5]}
                                        zoom={6}
                                        style={{ width: "100%", height: "100%", backgroundColor: "transparent" }}
                                        scrollWheelZoom={false}
                                    >
                                        <GeoJSON data={geoData} style={mapBaseStyle} />
                                        <MapBoundsUpdater geoData={geoData} />

                                        {finalMarkers[activeTab].map((marker) => (
                                            <CircleMarker
                                                key={marker.id}
                                                center={marker.position}
                                                radius={5}
                                                pathOptions={{
                                                    fillColor: markerColor,
                                                    color: "#fff",
                                                    weight: 1.5,
                                                    opacity: 1,
                                                    fillOpacity: 0.9,
                                                }}
                                            >
                                                <Popup>
                                                    <span className="font-semibold">{activeTab} Focus:</span>
                                                    <br /> {marker.label}
                                                </Popup>
                                            </CircleMarker>
                                        ))}

                                        <MapLegend markerColor={markerColor} activeTab={activeTab} />
                                    </MapContainer>
                                )}
                            </div>

                            {/* SIDE PANEL */}
                            <div className="lg:col-span-4">
                                <ScreeningProgressBars data={screeningData} />
                            </div>

                        </div>
                    </div>

                    {/* ⭐ RIGHT SIDE — CHARTS (6 Columns) */}
                    <div className="lg:col-span-6 grid grid-cols-1 lg:grid-cols-2 gap-2">

                        {/* Row 1: Donut Chart & Bar Chart */}
                        <div className="lg:col-span-1">
                            <StyledDonutChart data={hrAvailabilityData} />
                        </div>

                        <div className="lg:col-span-1">
                            <TopDrugsBarChart />
                        </div>


                        {/* Row 2: Stock Out Area Chart (Full Width) */}
                        <div className="lg:col-span-2">
                            <StockOutAreaChart />
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
};

export default IntegratedHealthDashboard;